import {
  canOptimizeImageUrl,
  GAME_PANEL_IMAGE_QUALITY,
  GAME_PANEL_IMAGE_WIDTH,
  getOptimizedImageUrl,
  getProviderSizedImageUrl,
  getReviewedImageUrl,
  getRetryImageUrl,
  normalizeImageUrl,
  shouldLoadProviderImageDirectly,
} from "@/lib/optimized-image"

// Keep a decoded look-ahead window alive. Retaining the Image objects
// avoids a visible decode pause when React swaps to the next round.
const MAX_PRELOADED_IMAGES = 32
const NEXT_IMAGE_WIDTHS = [384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840]
type PreloadEntry = {
  image: HTMLImageElement | null
  ready: Promise<boolean>
}
const preloaded = new Map<string, PreloadEntry>()
type ImagePreloadPriority = "high" | "low"

function getResponsivePanelWidth() {
  if (typeof window === "undefined") return GAME_PANEL_IMAGE_WIDTH

  const cssWidth =
    window.innerWidth >= 768 ? window.innerWidth / 2 : window.innerWidth
  const requestedWidth = Math.ceil(
    cssWidth * Math.max(1, window.devicePixelRatio || 1)
  )

  return Math.min(
    NEXT_IMAGE_WIDTHS.find((width) => width >= requestedWidth) ??
      NEXT_IMAGE_WIDTHS[NEXT_IMAGE_WIDTHS.length - 1],
    GAME_PANEL_IMAGE_WIDTH
  )
}

function rememberPreload(imageUrl: string, entry: PreloadEntry) {
  preloaded.set(imageUrl, entry)
  if (preloaded.size <= MAX_PRELOADED_IMAGES) return

  const oldest = preloaded.entries().next().value as
    | [string, PreloadEntry]
    | undefined
  if (!oldest) return
  preloaded.delete(oldest[0])
}

function loadImageUrl(
  imageUrl: string,
  priority: ImagePreloadPriority = "low"
) {
  if (preloaded.has(imageUrl)) {
    const existing = preloaded.get(imageUrl)!
    if (existing.image && priority === "high") {
      existing.image.fetchPriority = "high"
    }
    return existing.ready
  }

  if (typeof Image === "undefined") {
    return Promise.resolve(false)
  }

  const img = new Image()
  img.decoding = "async"
  img.fetchPriority = priority
  const ready = new Promise<boolean>((resolve) => {
    img.onload = () => {
      // Resolve after decode when supported so the next React panel can paint
      // the bitmap immediately instead of flashing its fallback background.
      void img.decode().then(
        () => resolve(true),
        () => resolve(true)
      )
    }
    img.onerror = () => {
      preloaded.delete(imageUrl)
      resolve(false)
    }
  })
  rememberPreload(imageUrl, { image: img, ready })
  img.src = imageUrl
  return ready
}

function getPreloadCandidates(
  url: string | null | undefined,
  targetWidth: number
) {
  const normalizedSource = normalizeImageUrl(url)
  if (!normalizedSource) return []

  const reviewedSource = getReviewedImageUrl(normalizedSource)
  const providerSizedSource = getProviderSizedImageUrl(
    reviewedSource,
    targetWidth
  )
  const primarySource = shouldLoadProviderImageDirectly(providerSizedSource)
    ? providerSizedSource
    : getOptimizedImageUrl(normalizedSource, {
        // Match the candidate the responsive game panel will choose. Preloading
        // a fixed desktop URL makes phones download a second, unused image.
        width: targetWidth,
        quality: GAME_PANEL_IMAGE_QUALITY,
      })

  const candidates = [primarySource]
  const retrySource = getRetryImageUrl(normalizedSource)
  if (retrySource) {
    candidates.push(
      getOptimizedImageUrl(retrySource, {
        width: targetWidth,
        quality: GAME_PANEL_IMAGE_QUALITY,
      })
    )
  }
  if (canOptimizeImageUrl(normalizedSource)) {
    candidates.push(getProviderSizedImageUrl(normalizedSource, targetWidth))
  }

  return [...new Set(candidates.filter((candidate): candidate is string => Boolean(candidate)))]
}

export async function preloadImage(
  url: string | null | undefined,
  priority: ImagePreloadPriority = "low"
) {
  const candidates = getPreloadCandidates(url, getResponsivePanelWidth())
  for (const candidate of candidates) {
    if (await loadImageUrl(candidate, priority)) return true
  }
  return false
}

export async function waitForImageReady(
  url: string | null | undefined,
  timeoutMs = 1000
) {
  let timeout: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<boolean>((resolve) => {
    timeout = setTimeout(() => resolve(false), timeoutMs)
  })
  const loaded = await Promise.race([
    preloadImage(url, "high"),
    timeoutPromise,
  ])
  if (timeout) clearTimeout(timeout)
  return loaded
}

export function preloadImages(
  urls: (string | null | undefined)[],
  priority: ImagePreloadPriority = "low"
) {
  return Promise.all(urls.map((url) => preloadImage(url, priority)))
}
